import { Graphics } from "pixi.js";

/**
 * CardBackgroundPattern - Creates fancy card background patterns
 * Low coupling: Independent utility class for card background rendering
 */
export class CardBackgroundPattern {
  public static readonly CORNER_RADIUS = 6;

  /**
   * Draws a fancy face-up card background pattern to the provided Graphics object
   */
  static drawFaceUpPattern(graphics: Graphics, width: number, height: number): void {
    graphics.clear();

    // Base white card background with rounded corners
    graphics.roundRect(0, 0, width, height, this.CORNER_RADIUS);
    graphics.fill(0xffffff);
    graphics.stroke({ width: 2, color: 0x000000 });

    // Add subtle inner border for depth (with rounded corners)
    graphics.roundRect(3, 3, width - 6, height - 6, this.CORNER_RADIUS - 1);
    graphics.stroke({ width: 1, color: 0x000000, alpha: 0.1 });

    // Add subtle texture pattern (very light)
    const textureColor = 0x000000;
    const textureAlpha = 0.03;
    
    // Subtle diagonal lines
    for (let i = -height; i < width + height; i += 8) {
      graphics.moveTo(i, 4);
      graphics.lineTo(i + height - 8, height - 4);
      graphics.stroke({ width: 0.5, color: textureColor, alpha: textureAlpha });
    }

    // Subtle corner decorations
    const cornerSize = 4;
    const cornerOffset = 6;
    
    // Top-left corner accent
    graphics.moveTo(cornerOffset, cornerOffset);
    graphics.lineTo(cornerOffset + cornerSize, cornerOffset);
    graphics.lineTo(cornerOffset, cornerOffset + cornerSize);
    graphics.stroke({ width: 0.5, color: textureColor, alpha: 0.1 });
    
    // Bottom-right corner accent
    graphics.moveTo(width - cornerOffset, height - cornerOffset);
    graphics.lineTo(width - cornerOffset - cornerSize, height - cornerOffset);
    graphics.lineTo(width - cornerOffset, height - cornerOffset - cornerSize);
    graphics.stroke({ width: 0.5, color: textureColor, alpha: 0.1 });
  }

  /**
   * Draws a fancy face-down card background pattern to the provided Graphics object
   */
  static drawFaceDownPattern(graphics: Graphics, width: number, height: number): void {
    graphics.clear();

    // Base card background with gradient effect and rounded corners
    graphics.roundRect(0, 0, width, height, this.CORNER_RADIUS);
    graphics.fill(0x1a5d2e);
    graphics.stroke({ width: 2, color: 0xffffff });

    // Outer border highlight (with rounded corners)
    graphics.roundRect(2, 2, width - 4, height - 4, this.CORNER_RADIUS - 1);
    graphics.stroke({ width: 1, color: 0x2a7d4e, alpha: 0.5 });

    // Inner border (with rounded corners)
    const borderPadding = 8;
    graphics.roundRect(borderPadding, borderPadding, width - borderPadding * 2, height - borderPadding * 2, this.CORNER_RADIUS - 2);
    graphics.stroke({ width: 1, color: 0x0a3d1e });

    // Create diamond pattern
    const centerX = width / 2;
    const centerY = height / 2;
    const diamondSize = 12;
    const spacing = 16;

    // Draw diamond grid pattern
    for (let row = -2; row <= 2; row++) {
      for (let col = -2; col <= 2; col++) {
        const x = centerX + col * spacing;
        const y = centerY + row * spacing;
        
        // Draw diamond shape
        graphics.moveTo(x, y - diamondSize / 2);
        graphics.lineTo(x + diamondSize / 2, y);
        graphics.lineTo(x, y + diamondSize / 2);
        graphics.lineTo(x - diamondSize / 2, y);
        graphics.closePath();
        graphics.stroke({ width: 0.5, color: 0x0a3d1e, alpha: 0.4 });
      }
    }

    // Add corner decorative elements
    const cornerSize = 8;
    const cornerOffset = 12;
    
    // Top-left corner
    this.drawCornerDecoration(graphics, cornerOffset, cornerOffset, cornerSize);
    // Top-right corner
    this.drawCornerDecoration(graphics, width - cornerOffset, cornerOffset, cornerSize);
    // Bottom-left corner
    this.drawCornerDecoration(graphics, cornerOffset, height - cornerOffset, cornerSize);
    // Bottom-right corner
    this.drawCornerDecoration(graphics, width - cornerOffset, height - cornerOffset, cornerSize);

    // Add subtle diagonal lines for texture
    const lineSpacing = 6;
    const lineColor = 0x0a3d1e;
    const lineAlpha = 0.2;
    
    for (let i = -height; i < width + height; i += lineSpacing) {
      graphics.moveTo(i, borderPadding);
      graphics.lineTo(i + height - borderPadding * 2, height - borderPadding);
      graphics.stroke({ width: 0.5, color: lineColor, alpha: lineAlpha });
    }

    // Add subtle vertical texture lines
    for (let x = borderPadding; x < width - borderPadding; x += 4) {
      graphics.moveTo(x, borderPadding);
      graphics.lineTo(x, height - borderPadding);
      graphics.stroke({ width: 0.3, color: lineColor, alpha: 0.15 });
    }
  }

  /**
   * Creates a mask to clip patterns within card boundaries (with rounded corners)
   */
  static createCardMask(width: number, height: number): Graphics {
    const mask = new Graphics();
    mask.name = "CardMask";
    mask.roundRect(0, 0, width, height, this.CORNER_RADIUS);
    mask.fill(0xffffff);
    return mask;
  }

  /**
   * Creates a simple, performant shadow for the card
   */
  static createCardShadow(width: number, height: number): Graphics {
    const shadow = new Graphics();
    shadow.name = "CardShadow";
    
    const shadowOffsetX = 2;
    const shadowOffsetY = 3;
    
    // Simple single-layer shadow - much more performant than multi-layer approach
    shadow.roundRect(
      shadowOffsetX,
      shadowOffsetY,
      width,
      height,
      this.CORNER_RADIUS
    );
    shadow.fill({ color: 0x000000, alpha: 0.25 });
    
    return shadow;
  }

  private static drawCornerDecoration(pattern: Graphics, x: number, y: number, size: number): void {
    // Draw a decorative corner element (small diamond)
    pattern.moveTo(x, y - size);
    pattern.lineTo(x + size, y);
    pattern.lineTo(x, y + size);
    pattern.lineTo(x - size, y);
    pattern.closePath();
    pattern.stroke({ width: 1, color: 0x2a7d4e, alpha: 0.6 });
    
    // Inner fill
    pattern.circle(x, y, size / 3);
    pattern.fill({ color: 0x0a3d1e, alpha: 0.3 });
  }
}

